import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/en-gb";
import { Dayjs } from "dayjs";
import { Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import {
  InputOnChange,
  MultiselectOnChange,
  StepFormFieldProps,
  Taster,
} from "interfaces/tasters";

import { Button } from "components/common/Button";
import { Drawer } from "components/common/Drawer";
import { Input } from "components/common/inputs/Input";
import { Select } from "components/common/inputs/Select";
import { Multiselect } from "components/common/inputs/Multiselect";
import { InputPhone } from "components/common/inputs/InputPhone";
import { DatePicker } from "components/common/DatePicker";
import { Avatar } from "components/common/Avatar";

import {
  StyledTasterMainInfo,
  StyledTasterMenu,
  StyledTasterMenuContent,
  StyledTasterMenuForm,
  StyledTasterMenuStepBtnGroup,
} from "styles/components/taster-menu-drawer";
import { useWindowSize } from "hooks/window-size";

interface TasterMenuDrawerProps {
  open?: boolean;
  taster?: Taster;
  fields?: StepFormFieldProps[];
  loading?: boolean;
  validFieldErrors?: string[];

  onClose?: () => void;
  handleTasterUpdate?: () => void;
  handleTasterRemove?: () => void;
}

const TasterMenuDrawer: FC<TasterMenuDrawerProps> = ({
  open,
  taster,
  fields,
  loading,
  validFieldErrors,
  onClose,
  handleTasterUpdate,
  handleTasterRemove,
}) => {
  const { t } = useTranslation(); // Initialize translation
  const theme = useTheme();

  const { windowSize } = useWindowSize();

  const headerRef = useRef<HTMLDivElement>(null);

  const scrollToComponentWithError = useCallback(() => {
    if (
      validFieldErrors &&
      validFieldErrors.length > 0 &&
      headerRef &&
      headerRef.current
    ) {
      headerRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [validFieldErrors, headerRef]);

  useEffect(() => {
    scrollToComponentWithError();
  }, [scrollToComponentWithError]);

  const handleSaveBtnClick = useCallback(() => {
    scrollToComponentWithError();
    handleTasterUpdate?.();
  }, [handleTasterUpdate, scrollToComponentWithError]);

  const handleClose = useCallback(() => {
    if (loading) return;

    onClose?.();
  }, [loading, onClose]);

  const renderStepFormField = useCallback(
    (fieldProps: StepFormFieldProps): ReactElement => {
      const {
        id,
        type,
        value,
        placeholder,
        label,
        error,
        errorText,
        disabled,
        onBlur,
        onChange,
        onFocus,
        options,
        required,
      } = fieldProps;

      switch (type) {
        case "textarea":
          return (
            <Input
              id={id}
              multiline
              rows={4}
              value={value as string}
              onBlur={onBlur}
              onChange={onChange as InputOnChange}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              key={label}
              required={required}
            />
          );

        case "phone":
          return (
            <InputPhone
              value={value as string}
              onBlur={onBlur}
              onChange={onChange as InputOnChange}
              onFocus={onFocus}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              key={label}
              required={required}
            />
          );

        case "select":
          return (
            <Select
              id={id}
              value={value as string}
              onChange={onChange as InputOnChange}
              options={options}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              color="grey"
              key={label}
            />
          );

        case "multiselect":
          return (
            <Multiselect
              id={id as string}
              value={value as string[]}
              onChange={onChange as MultiselectOnChange}
              options={options}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              color="grey"
              key={label}
            />
          );
        case "date":
          return (
            <DatePicker
              value={value as Dayjs}
              onChange={onChange as MultiselectOnChange}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              key={label}
              disableFuture
            />
          );

        default:
          return (
            <Input
              id={id}
              value={value as string}
              onBlur={onBlur}
              onChange={onChange as InputOnChange}
              placeholder={placeholder}
              label={label}
              error={error}
              errorText={errorText}
              disabled={disabled}
              key={label}
              required={required}
            />
          );
      }
    },
    []
  );

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      width={windowSize.width < 768 ? "100%" : "564px"}
    >
      <StyledTasterMenu className="taster-menu">
        <StyledTasterMenuContent>
          <StyledTasterMainInfo>
            <Avatar
              size="lg"
              src={taster?.avatar}
              name={
                taster && taster.firstName
                  ? `${taster?.firstName} ${taster?.lastName}`
                  : undefined
              }
              alt={taster?.firstName as string | undefined}
              className="taster-info__avatar"
            />
            <Typography variant="h2" color={theme.palette.common.black}>
              {t("taster_name_label", {
                firstName: taster?.firstName ?? "",
                lastName: taster?.lastName ?? "",
              })}
            </Typography>
          </StyledTasterMainInfo>
          <StyledTasterMenuForm ref={headerRef}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {fields?.map((field) => renderStepFormField(field))}
            </LocalizationProvider>
          </StyledTasterMenuForm>
        </StyledTasterMenuContent>
        <StyledTasterMenuStepBtnGroup>
          <Button
            variant="primary"
            size="md"
            disabled={loading}
            isLoading={loading}
            onClick={handleSaveBtnClick}
            fullWidth
          >
            {t("save_button")}
          </Button>
          <Button
            variant="secondary"
            size="md"
            disabled={loading}
            isLoading={loading}
            onClick={handleTasterRemove}
            fullWidth
          >
            {t("remove_button")}
          </Button>
        </StyledTasterMenuStepBtnGroup>
      </StyledTasterMenu>
    </Drawer>
  );
};

export default TasterMenuDrawer;
